/**
 * Normalise admin email thread rows for UI + list badges (shared by AdminEmailThreadPanel).
 *
 * @param {object} m
 * @param {string} clientEmail
 */
function normalizeEmailMessage(m, clientEmail) {
  const client = (clientEmail || "").trim().toLowerCase();
  let direction = m?.direction;
  if (direction === "in" || direction === "inbound") direction = "inbound";
  else if (direction === "out" || direction === "outbound") direction = "outbound";
  else {
    const from = (m?.from || m?.fromAddress || "").trim().toLowerCase();
    direction = from && client && from === client ? "inbound" : "outbound";
  }

  const base = {
    id: m?.id ?? `email-${m?.createdAt || Math.random()}`,
    direction,
    createdAt: m?.createdAt || m?.sentAt || new Date(0).toISOString(),
    subject: normalizeEmailHeaderText(m?.subject || ""),
    body: normalizeEmailBodyText((m?.body || m?.text || m?.html || "").trim()),
    from: m?.from || m?.fromAddress || "",
    to: m?.to || m?.toAddress || "",
  };
  if (m?.clientAnalysis && typeof m.clientAnalysis === "object") {
    const ca = { ...m.clientAnalysis };
    if (m.professionalSuggestion !== undefined && ca.professionalSuggestion === undefined) {
      ca.professionalSuggestion = m.professionalSuggestion;
    }
    if (m.budgetAssessment !== undefined && ca.budgetAssessment === undefined) {
      ca.budgetAssessment = m.budgetAssessment;
    }
    if (m.analysisSchemaVersion !== undefined && ca.analysisSchemaVersion === undefined) {
      ca.analysisSchemaVersion = m.analysisSchemaVersion;
    }
    return { ...base, clientAnalysis: ca };
  }
  return base;
}

function decodeEncodedWordChunk(chunk) {
  const m = /^=\?([^?]+)\?([bBqQ])\?([^?]*)\?=$/.exec(String(chunk || "").trim());
  if (!m) return String(chunk || "");
  const encoding = m[2].toUpperCase();
  const payload = m[3] || "";
  try {
    if (encoding === "B") {
      const bytes = Uint8Array.from(atob(payload), (c) => c.charCodeAt(0));
      return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    }
    const qp = payload.replace(/_/g, " ");
    return decodeQuotedPrintableText(qp);
  } catch {
    return String(chunk || "");
  }
}

function decodeRfc2047Header(text) {
  const t = String(text || "").trim();
  if (!t.includes("=?")) return t;
  return t
    .replace(/=\?[^?]+\?[bBqQ]\?[^?]*\?=/g, (token) => decodeEncodedWordChunk(token))
    .replace(/\s{2,}/g, " ")
    .trim();
}

function decodeQuotedPrintableText(text) {
  const t = String(text || "");
  const softWrapped = t.replace(/=\r?\n/g, "");
  let out = "";
  for (let i = 0; i < softWrapped.length; i += 1) {
    const ch = softWrapped[i];
    if (ch === "=" && /^[A-Fa-f0-9]{2}$/.test(softWrapped.slice(i + 1, i + 3))) {
      out += String.fromCharCode(parseInt(softWrapped.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      out += ch;
    }
  }
  try {
    const bytes = Uint8Array.from(out.split("").map((c) => c.charCodeAt(0)));
    return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  } catch {
    return out;
  }
}

function collapseWhitespacePerLine(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/[ \t]+/g, " ").trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function stripMimeBoundaryAndPartHeaders(text) {
  const lines = String(text || "").split(/\r?\n/);
  const kept = [];
  let inPartHeaders = false;
  for (const rawLine of lines) {
    const line = rawLine || "";
    if (/^--[-_0-9A-Za-z]{8,}/.test(line.trim())) continue;
    if (/^Content-Type:/i.test(line) || /^Content-Transfer-Encoding:/i.test(line)) {
      inPartHeaders = true;
      continue;
    }
    if (inPartHeaders) {
      if (!line.trim()) {
        inPartHeaders = false;
      }
      continue;
    }
    kept.push(line);
  }
  return kept.join("\n");
}

function pickReadableMimePart(rawBody) {
  const body = String(rawBody || "");
  if (!/Content-Type:/i.test(body)) return body;
  const htmlMatch = /Content-Type:\s*text\/html[\s\S]*?(?:\r?\n\r?\n)([\s\S]+)/i.exec(body);
  if (htmlMatch && htmlMatch[1]) return htmlMatch[1];
  const textMatch = /Content-Type:\s*text\/plain[\s\S]*?(?:\r?\n\r?\n)([\s\S]+)/i.exec(body);
  if (textMatch && textMatch[1]) return textMatch[1];
  return body;
}

function normalizeEmailBodyText(rawBody) {
  const picked = pickReadableMimePart(rawBody);
  const withoutPartHeaders = stripMimeBoundaryAndPartHeaders(picked);
  const decoded = decodeQuotedPrintableText(withoutPartHeaders);
  return collapseWhitespacePerLine(decoded);
}

function normalizeEmailHeaderText(rawHeader) {
  const decoded = decodeRfc2047Header(rawHeader);
  return collapseWhitespacePerLine(decoded);
}

/**
 * @param {unknown} raw
 * @param {string} clientEmail
 */
export function normalizeEmailThread(raw, clientEmail) {
  const list = Array.isArray(raw) ? raw : [];
  const mapped = list.map((m) => normalizeEmailMessage(m, clientEmail));
  return mapped.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

/** @param {{ status?: string } | null | undefined} ca */
function inboundAnalysisUiKind(ca) {
  if (!ca || typeof ca !== "object") return "waiting";
  const s = String(ca.status || "").toLowerCase();
  if (s === "pending") return "pending";
  if (s === "failed") return "failed";
  if (s === "completed") return "completed";
  return "waiting";
}

/**
 * Latest inbound message with completed analysis and a non-unclear offer signal.
 *
 * @param {Array<{ direction?: string, createdAt?: string, clientAnalysis?: object }>} thread
 * @returns {{ signal: string, label: string } | null}
 */
export function pickLatestInboundOfferInsight(thread) {
  const completed = thread.filter(
    (m) => m.direction === "inbound" && inboundAnalysisUiKind(m.clientAnalysis) === "completed"
  );
  completed.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  for (const m of completed) {
    const sig = m.clientAnalysis?.offerSignal;
    const label = (m.clientAnalysis?.offerLabel || "").trim();
    if (sig && String(sig).trim() !== "" && String(sig) !== "unclear") {
      return { signal: sig, label: label || String(sig) };
    }
  }
  return null;
}
